import { useCallback, useRef, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import store from "../State/Store/store";
import StorageManager from "../utils/StorageManager";
import TickerTimeout from "../utils/TickerTimeout";
import {
  setWinAnimationsPlaying,
  setResetMainGameVisualEffects,
  setCounterAnimationStarted,
  setCounterAnimationFinished,
  setCriticalAnimationPlaying,
  setExpandingWildsOccurred,
  setSkipWinAnimation,
  setUserWantsToDismissWinAnimation,
  setSkipCounterAnimation,
  setIsWaitingForUserConfirmation,
  closeAllPopups,
  setSymbolPopupBlocked,
  openTurboSuggestionPopup,
  setTurboSuggestionPending,

  setIsAnimatedWin,
  setUserInterfaceDisabled,
  setShowFreeSpinIntro,
  setShowRoyalCollectIntro,
  setShowPotCollectionIntro,
} from "../State/actions";
import {
  selectResetMainGameVisualEffects,
  selectCurrentRound,
  selectWinningLines,

  selectSpinComplete,
  selectIsSpinning,
  selectTurboSuggestionPending,
  selectFreeSpinActive,

  selectAutoPlayActive,
  selectIntroPopupActive,
  selectInfoPopupActive,
  selectWinAnimationsPlaying,

  selectFreeSpinTriggered,
  selectRoyalCollectTriggered,
  selectPotCollectionTriggered,
  selectReplaySessionActive,
} from "../State/selectors";
import { SOUND_NAME } from "../sounds";

import { useReelRefs } from "../Contexts/ReelRefsContext";
import { useReelControl } from "../Contexts/ReelControlContext";
import { GAME_LAYOUT } from "../settings";
import { WILD_SYMBOL_ID } from "../SymbolData";
import {
  FEATURES_CONFIG,
  playCascadingExpandingWild,
  playFullReelExpandingWild,
} from "../featuresConfig";
import { soundManager } from "../SoundManager";
import SpineAnimationData from "../utils/SpineAnimationData";

// Hook for managing win line animations, sound effects, and reel coordination
export const useWinController = () => {
  const {
    reelRefs,
    overlayReelRefs,
    winLinesContainerRef,
    winBoxContainerRef,
    dimReelSetRef,
    globalLineWinTextRef,
    lineWinTextRef,
    characterAnimationControllerRef,
    potChargingAnimatorRef,
    snapClearAllRef,
  } = useReelRefs();

  const { spacebarTurboActive, spacebarTurboActiveRef } = useReelControl();

  const dispatch = useDispatch();

  const spinComplete = useSelector(selectSpinComplete);
  const isSpinning = useSelector(selectIsSpinning);
  const currentRound = useSelector(selectCurrentRound);
  const winningLines = useSelector(selectWinningLines);

  const turboSuggestionPending = useSelector(selectTurboSuggestionPending);
  const freeSpinActive = useSelector(selectFreeSpinActive);
  const resetMainGameVisualEffects = useSelector(
    selectResetMainGameVisualEffects,
  );
  const autoPlayActive = useSelector(selectAutoPlayActive);
  const introPopupActive = useSelector(selectIntroPopupActive);
  const infoPopupActive = useSelector(selectInfoPopupActive);
  const freeSpinTriggered = useSelector(selectFreeSpinTriggered);
  const royalCollectTriggered = useSelector(selectRoyalCollectTriggered);
  const potCollectionTriggered = useSelector(selectPotCollectionTriggered);


  // Refs for state management and animation control
  const reelsetControllerRef = useRef(null);
  const currentTimeoutsRef = useRef([]);
  const pendingAnimationFramesRef = useRef([]);
  const isAnimatingRef = useRef(false);
  const isReadyRef = useRef(false);
  const animationCompletedRef = useRef(false);
  // Tracks the round ID that was fast-pathed in super turbo.
  // The recovery effect uses this to undo the fast path if the user releases
  // spacebar within the same round (keyup fires AFTER the fast path committed).
  const fastPathRoundRef = useRef(null);
  const isFirstAnimationRef = useRef(true);
  const spinCompleteRef = useRef(spinComplete);
  const currentRoundRef = useRef(currentRound);
  const winningLinesRef = useRef(winningLines);
  const winningDataRef = useRef(null);
  const lineIdsRef = useRef([]);
  const currentLineIndexRef = useRef(0);
  const dispatchRef = useRef(dispatch);

  const turboActiveRef = useRef(false);
  const infoPopupActiveRef = useRef(infoPopupActive);
  const winSoundPlayedRef = useRef(false);
  const expandingWildReelIndicesRef = useRef([]);
  const animatedRoundsRef = useRef(new Set());

  const executeOnReelControllersRef = useRef(null);
  const executeOnOverlayReelControllersRef = useRef(null);
  const currentIntervalsRef = useRef([]);
  const expandingWildAnimationStateRef = useRef({});
  const currentSpineListenerRef = useRef(null);
  const useFastWinRef = useRef(false); // Sticky flag: true if this spin entered with win_fast (persists into line cycling)
  const prevWinPosSetRef = useRef(null); // Previous line's winning positions for diff-based transitions


  // Memoize simple derived values
  const hasWins = useMemo(
    () => currentRound?.win && currentRound.win > 0,
    [currentRound?.win],
  );

  const hasScatterWin = (currentRound?.scatterCount || 0) > 0;


  const winType = currentRound?.winType || "";

  // ── The celebration, and the one case it is withheld ──
  //
  // `winAlreadyPresented` is set by gameSettingsSlice when it hands the live
  // round back at the end of a replay session. That round was celebrated
  // before the player opened the history, so playing the tier chain again is
  // a second fanfare for money paid minutes ago.
  //
  // Suppressed HERE and nowhere else, because this is the single place the
  // flag is derived and every `setWinAnimationsPlaying(true)` in this file is
  // gated on it. Everything else about the round is untouched: the win lines
  // still cycle, the win boxes still draw, the amount still shows and the
  // balance still updates. Only the big-win animation is withheld.
  const isAnimatedWin =
    (currentRound?.isAnimatedWin && !currentRound?.winAlreadyPresented) || false;



  /**
   * Clears all active timeouts, intervals, and pending animation frames.
   * Prevents memory leaks and race conditions when animations are interrupted or component unmounts.
   */
  const clearAllTimeouts = useCallback(() => {
    const timeouts = currentTimeoutsRef.current;
    for (let i = 0; i < timeouts.length; i++) {
      const t = timeouts[i];
      if (t && typeof t.clear === "function") {
        t.clear();
      } else {
        clearTimeout(t);
      }
    }

    currentTimeoutsRef.current = [];

    const intervals = currentIntervalsRef.current;
    for (let i = 0; i < intervals.length; i++) {
      const t = intervals[i];
      if (t && typeof t.clear === "function") {
        t.clear();
      } else {
        clearInterval(t);
      }
    }
    currentIntervalsRef.current = [];

    const animationFrames = pendingAnimationFramesRef.current;
    for (let i = 0; i < animationFrames.length; i++) {
      cancelAnimationFrame(animationFrames[i]);
    }
    pendingAnimationFramesRef.current = [];

    // Remove any pending spine complete listener to prevent stale resolves
    if (currentSpineListenerRef.current) {
      const { spineInstance, listener } = currentSpineListenerRef.current;
      try {
        spineInstance?.state?.removeListener(listener);
      } catch {
        // spine may already be destroyed
      }
      currentSpineListenerRef.current = null;
    }
  }, []);





  // ── Leaving a replay: hand the presentation refs back ──
  //
  // These are REFS, not Redux, so none of the slice-level restore reaches them.
  // `isAnimatingRef` in particular is latched true by the presentation that ran
  // BEFORE the player opened the history, and nothing lowers it — leaving a
  // replay is not a spin, and the visual-reset path that normally clears these
  // is not dispatched here either. The main effect below then returns at its
  // top gate on every pass, and the restored round shows no win at all.
  //
  // The same four resets `resetMainGameVisualEffects` performs, for the same
  // reason: the board is being handed back and the round has to be free to
  // present again. `animatedRoundsRef` is cleared deliberately — leave it and
  // the round is still marked as shown, so the dedupe blocks it instead.
  //
  // The CELEBRATION stays withheld: that is `winAlreadyPresented` on the round,
  // read where `isAnimatedWin` is derived. This only restores the ability to
  // present at all.
  const replaySessionActive = useSelector(selectReplaySessionActive);
  const prevReplaySessionForWinRef = useRef(replaySessionActive);
  useEffect(() => {
    const prev = prevReplaySessionForWinRef.current;
    prevReplaySessionForWinRef.current = replaySessionActive;

    // ── Falling edge ONLY — leaving replay, not entering it ──
    //
    // An entry-side version of this was tried and reverted. Cancelling the
    // live presentation on the way IN kills `cycleThroughLines` mid-flight,
    // and that loop is what tidies up after itself: the board was left DIMMED
    // with the boxes and the win text already gone, which reads as the game
    // having broken rather than as a replay starting.
    //
    // Entering a replay does not need the presentation torn down. The replay
    // loads its own round and paints its own board over the top; the only
    // thing that had to change on this edge was the stale win NUMBER, and
    // animationsSlice clears those flags on startReplaySession.
    if (!prev || replaySessionActive) return;

    if (isAnimatingRef.current) {
      isAnimatingRef.current = false;
      clearAllTimeouts();
    }
    animationCompletedRef.current = false;
    isFirstAnimationRef.current = true;
    animatedRoundsRef.current.clear();
  }, [replaySessionActive, clearAllTimeouts]);

  /**
   * Main effect: Orchestrates win animations after spin completes.
   * Handles expanding wilds, normal wins, and Big Win animations.
   */
  useEffect(() => {
    if (
      !isReadyRef.current ||
      !reelsetControllerRef.current ||
      !spinComplete ||
      isAnimatingRef.current ||
      animationCompletedRef.current ||
      introPopupActive ||
      // FR info popup: only block animated wins (Big Win overlay clashes with popup).
      // Base wins show immediately underneath the popup — no visual conflict.
      (infoPopupActive && isAnimatedWin)
    ) {
      return;
    }




    if (freeSpinActive) {
      return;
    }


    if (!currentRound?.triggerWin || !winningLines) {

      // On no-win rounds, wait for any in-flight POT charging bolts to complete
      // before allowing autoplay to fire the next spin. Without this, autoplay
      // starts the next spin while bolts are mid-charge, canceling them visually.
      if (FEATURES_CONFIG.POT_CHARGING.ENABLED && potChargingAnimatorRef?.current) {
        const animator = potChargingAnimatorRef.current;
        if (animator._activeBolts.length > 0) {
          dispatch(setCriticalAnimationPlaying(true));
          animator.waitForAllTrails().then(() => {
            dispatch(setCriticalAnimationPlaying(false));
          });
        }
      }
      return;
    }

    const winTypeLocal = winType;
    const isAnimatedWinLocal = isAnimatedWin;
    const hasScatterWinLocal = hasScatterWin;

    const expandingWildReels =
      FEATURES_CONFIG.EXPANDING_WILDS.ENABLED && !hasScatterWinLocal
        ? currentRound.expandingWildReels || []
        : [];
    const hasExpandingWilds = expandingWildReels.length > 0;

    const expandingWildReelIndices = expandingWildReels.map(
      ({ reelIndex }) => reelIndex,
    );
    expandingWildReelIndicesRef.current = expandingWildReelIndices;

    const wins = currentRound?.wins;
    const winsHash =
      Array.isArray(wins) && wins.length > 0 ? JSON.stringify(wins) : null;
    const roundId = currentRound?.id || "unknown";
    const uniqueAnimationId = `${roundId}-${winsHash}`;

    if (animatedRoundsRef.current.has(uniqueAnimationId)) {
      // Mark animation as completed to unblock UI
      animationCompletedRef.current = true;
      return;
    }

    animatedRoundsRef.current.add(uniqueAnimationId);

    // Lock immediately to prevent effect re-entry during TickerTimeout delay
    isAnimatingRef.current = true;


    // FREE SPIN TRIGGER: Disable entire UI immediately so user can't interact
    // during win processing of a trigger round (3+ scatters)
    if (FEATURES_CONFIG.FREE_SPINS.ENABLED && freeSpinTriggered) {
      dispatch(setUserInterfaceDisabled(true));
      dispatch(setCriticalAnimationPlaying(true));
    }

    // ROYAL COLLECT TRIGGER: Disable entire UI immediately so user can't interact
    // during win processing of a trigger round (2 coins + 1 royal)
    if (FEATURES_CONFIG.ROYAL_COLLECT.ENABLED && royalCollectTriggered) {
      dispatch(setUserInterfaceDisabled(true));
      dispatch(setCriticalAnimationPlaying(true));
    }

    // POT COLLECTION TRIGGER: Same pattern — disable UI during trigger wins
    if (FEATURES_CONFIG.POT_COLLECTION.ENABLED && potCollectionTriggered) {
      dispatch(setUserInterfaceDisabled(true));
      dispatch(setCriticalAnimationPlaying(true));
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // TURBO WIN FAST PATH (HOLD OR AUTOPLAY)
    // Activates when user is actively holding spacebar/button (turbo or super turbo)
    // OR autoplay is running in super turbo mode.
    // Single-press in super turbo mode falls through to normal win processing below.
    // Skip all win animations for regular wins — show value, unblock spin immediately.
    // Animated wins (Big Win, Mega Win) and feature triggers still go through normal pipeline.
    // ═══════════════════════════════════════════════════════════════════════════
    const currentSpeed = store.getState().settings.autoPlaySpeed;
    // Read from REF (synchronous) — React state can be stale if user released
    // spacebar between spin-fire and win-processing. The ref updates instantly.
    const isActivelyHolding = spacebarTurboActiveRef.current;
    const isAutoPlaySuperTurbo =
      store.getState().settings.autoPlayActive && currentSpeed === "super_turbo";


    if (
      currentSpeed === "super_turbo" &&
      (isActivelyHolding || isAutoPlaySuperTurbo) &&
      !isAnimatedWinLocal &&
      !freeSpinTriggered &&
      !royalCollectTriggered &&
      !potCollectionTriggered &&
      !hasExpandingWilds
    ) {
      // ─────────────────────────────────────────────────────────────────────
      // SUPER TURBO FAST PATH — skip animations, show value, unblock spin.
      //
      // RACE CONDITION SAFETY: The user's keyup event may still be in transit
      // (finger physically leaving the key). If they release within ~100ms,
      // the Recovery Effect below detects the spacebarTurboActive→false
      // transition, undoes this fast path, and runs normal win processing.
      // ─────────────────────────────────────────────────────────────────────

      // Signal WinDisplay to show the final value immediately
      dispatch(setCounterAnimationStarted(true));
      dispatch(setCounterAnimationFinished(true));
      dispatch(setCriticalAnimationPlaying(false));

      // Flash WinText on the reelset (both spacebar hold and autoplay super turbo)
      if (
        FEATURES_CONFIG.WIN_LINE_DIGIT.ENABLED &&
        globalLineWinTextRef?.current &&
        currentRound?.win
      ) {
        globalLineWinTextRef.current.showWinAmount(currentRound.win, {
          skipIntro: false,
          targetDuration: 700,
        });
      }

      isAnimatingRef.current = false;
      animationCompletedRef.current = true;

      // Tag this round for potential recovery if user releases right after
      fastPathRoundRef.current = uniqueAnimationId;


      return () => clearAllTimeouts();
    }



    // CRITICAL: Block spin button IMMEDIATELY before the WIN_LINES_DELAY
    // Without this, there's a 200ms gap where counterAnimationStarted is false
    // and the user can click spin before win processing starts
    dispatch(setCounterAnimationStarted(true));
    // Also hold WinDisplay hidden during the delay — it checks !criticalAnimationPlaying,
    // so it won't show until setCriticalAnimationPlaying(false) fires inside the TickerTimeout
    dispatch(setCriticalAnimationPlaying(true));

    new TickerTimeout(async () => {
      // Wait for POT charging trails to finish before processing wins
      if (FEATURES_CONFIG.POT_CHARGING.ENABLED && potChargingAnimatorRef?.current) {
        await potChargingAnimatorRef.current.waitForAllTrails();
      }

      if (hasExpandingWilds) {
        dispatch(setExpandingWildsOccurred(true));
        dispatch(setCriticalAnimationPlaying(true));

        // All expanding wilds play the same sound
        soundManager.play(SOUND_NAME.expanding_wild);

        const animationMode = FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE;

        (async () => {
          let animationPromises = [];

          if (animationMode === "full_reel") {
            animationPromises = expandingWildReels.map(({ reelIndex }) =>
              playFullReelExpandingWild(reelIndex, reelRefs, overlayReelRefs),
            );
          } else if (animationMode === "cascade") {
            animationPromises = expandingWildReels.map(
              ({ reelIndex, wildPosition }) =>
                playCascadingExpandingWild(
                  reelIndex,
                  wildPosition,
                  reelRefs,
                  overlayReelRefs,
                  currentTimeoutsRef,
                  dispatch,
                ),
            );
          }

          // Wait for all expanding wild "appear" animations to complete (event-driven)
          if (animationPromises.length > 0) {
            await Promise.all(animationPromises);
          }

          // Hide cascade animations if needed
          if (animationMode === "cascade") {
            executeOnOverlayReelControllersRef.current(
              (overlayReelController) => {
                overlayReelController?.hideAllAnimations();
              },
            );
          }

          dispatch(setCriticalAnimationPlaying(false));

          const winningData =
            reelsetControllerRef.current.matchHorizontalWinningLines(
              winningLines,
              currentRound.wins,
            );

          if (winningData && Object.keys(winningData).length > 0) {
            dispatch(setIsAnimatedWin(isAnimatedWinLocal));
            dispatch(setCounterAnimationStarted(true));

            if (FEATURES_CONFIG.EXPANDING_WILDS.SHOW_ALL_WINS_BEFORE_CYCLING) {
              if (winTypeLocal && !winSoundPlayedRef.current) {
                if (isAnimatedWinLocal) {
                  winSoundPlayedRef.current = true;
                }
              }

              if (isAnimatedWinLocal) {
                dispatch(closeAllPopups());
                dispatch(setSymbolPopupBlocked(true));
                dispatch(setWinAnimationsPlaying(true));
              }

              const shouldProceed = await showAllWinningSymbolsOnceRef.current(
                winningData,
                true,
                expandingWildReelIndices,
                winTypeLocal,
                isAnimatedWinLocal,
              );

              if (shouldProceed && !spacebarTurboActiveRef.current) {
                startWinningLinesAnimationRef.current(winningData);
              } else {
                if (
                  FEATURES_CONFIG.WIN_LINES.ENABLED &&
                  winLinesContainerRef.current
                ) {
                  winLinesContainerRef.current.hideWinLine();
                }
                if (globalLineWinTextRef?.current) {
                  globalLineWinTextRef.current.hide();
                }
                // NEW - unified line win text
                if (lineWinTextRef?.current) {
                  lineWinTextRef.current.hide();
                }
              }
            }

            if (!FEATURES_CONFIG.EXPANDING_WILDS.SHOW_ALL_WINS_BEFORE_CYCLING) {
              if (isAnimatedWinLocal) {
                dispatch(closeAllPopups());
                dispatch(setSymbolPopupBlocked(true));
                dispatch(setWinAnimationsPlaying(true));
              }
            }
          }
        })();
      } else {
        const winningData =
          reelsetControllerRef.current.matchHorizontalWinningLines(
            winningLines,
            currentRound.wins,
          );

        if (!winningData || Object.keys(winningData).length === 0) {
          return;
        }

        dispatch(setCriticalAnimationPlaying(true));

        const shouldShowAllWins = FEATURES_CONFIG.SHOW_ALL_WINS.ENABLED;
        const isScatterWin = currentRound?.type === "feature";
        const skipShowAllWins =
          isScatterWin && FEATURES_CONFIG.SHOW_ALL_WINS.EXCLUDE_SCATTER;

        // Skip show-all when the only win(s) are trigger symbols (coins/scatters).
        // No point showing all + holding when we can go straight to cycling the trigger line.
        // This avoids the visual jitter from short trigger animations (coins) looping only once
        // within the wild-based display window.
        const lineIds = Object.keys(winningData);
        const isTriggerOnly = lineIds.length > 0 && lineIds.every((lineId) => {
          const data = winningData[lineId];
          return data.isCoin || data.isScatter;
        });
        const skipShowAllForTriggerOnly = shouldShowAllWins && isTriggerOnly;

        (async () => {

          dispatch(setCriticalAnimationPlaying(false));
          dispatch(setIsAnimatedWin(isAnimatedWinLocal));
          dispatch(setCounterAnimationStarted(true));

          // Trigger character "yes" animation on every win
          if (FEATURES_CONFIG.CHARACTER_ANIMATION.ENABLED) {
            characterAnimationControllerRef.current?.playAnimation("yes");
          }

          let shouldProceed = true;

          if (shouldShowAllWins && !skipShowAllWins && !skipShowAllForTriggerOnly) {
            if (winTypeLocal && !winSoundPlayedRef.current) {
              if (isAnimatedWinLocal) {
                winSoundPlayedRef.current = true;
              }
            }

            if (isAnimatedWinLocal) {
              dispatch(closeAllPopups());
              dispatch(setSymbolPopupBlocked(true));
              dispatch(setWinAnimationsPlaying(true));
            }

            shouldProceed = await showAllWinningSymbolsOnceRef.current(
              winningData,
              false,
              [],
              winTypeLocal,
              isAnimatedWinLocal,
            );
          }


          if (shouldProceed) {
            // When show-all is skipped, release the animation lock manually
            // (showAllWinningSymbolsOnce normally does this at its end)
            if (!shouldShowAllWins || skipShowAllWins || skipShowAllForTriggerOnly) {
              isAnimatingRef.current = false;
            }
            // During turbo/autoplay: skip per-line cycling — show-all is sufficient.
            // Autoplay continuation will snap-clear and fire next spin.
            if (!turboActiveRef.current) {
              startWinningLinesAnimationRef.current(winningData);
            } else {
              // Clean up show-all visuals so autoplay can proceed cleanly
              if (
                FEATURES_CONFIG.WIN_LINES.ENABLED &&
                winLinesContainerRef.current
              ) {
                winLinesContainerRef.current.hideWinLine();
              }
              if (globalLineWinTextRef?.current) {
                globalLineWinTextRef.current.hide();
              }
              if (lineWinTextRef?.current) {
                lineWinTextRef.current.hide();
              }
            }
          } else {
            if (
              FEATURES_CONFIG.WIN_LINES.ENABLED &&
              winLinesContainerRef.current
            ) {
              winLinesContainerRef.current.hideWinLine();
            }
            if (globalLineWinTextRef?.current) {
              globalLineWinTextRef.current.hide();
            }
            // NEW - unified line win text
            if (lineWinTextRef?.current) {
              lineWinTextRef.current.hide();
            }
          }

          if (!shouldShowAllWins || skipShowAllWins || skipShowAllForTriggerOnly) {
            // Play win sound for non-animated wins when show-all is skipped
            if (!isAnimatedWinLocal && SOUND_NAME.basic_win) {
              soundManager.play(SOUND_NAME.basic_win);
            }
            if (isAnimatedWinLocal) {
              dispatch(closeAllPopups());
              dispatch(setSymbolPopupBlocked(true));
              dispatch(setWinAnimationsPlaying(true));
            }
          }
        })();
      }
    }, FEATURES_CONFIG.WIN_ANIMATION_TIMING.WIN_LINES_DELAY);

    return () => {
      // Only clear timeouts when cycling is NOT in progress.
      // When infoPopupActive changes (info popup close), React re-runs this effect.
      // If cycling IS running (isAnimatingRef.current = true), clearing would kill it.
      // startWinningLinesAnimation already calls clearAllTimeouts on entry,
      // so skipping here is always safe for new spin starts.
      if (!isAnimatingRef.current) {
        clearAllTimeouts();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    spinComplete,
    currentRound,
    winningLines,
    reelRefs,
    overlayReelRefs,
    isSpinning,
    dispatch,
    introPopupActive,
    infoPopupActive,
  ]);

  // ═══════════════════════════════════════════════════════════════════════════
  // FAST PATH RECOVERY EFFECT
  //
  // Watches spacebarTurboActive transitions from true→false. If a fast path
  // was committed for the current round (fastPathRoundRef is set), this means
  // the user released spacebar AFTER the fast path already skipped animations.
  //
  // Recovery:
  // 1. Undo the fast path flags (animationCompleted, animatedRounds dedup)
  // 2. Reset counter animation states
  // 3. Run normal win processing with full animations
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    // Only act on deactivation: spacebar released OR autoplay ended.
    // If BOTH are still active, no recovery needed — fast path is valid.
    if (spacebarTurboActive || autoPlayActive) {
      return;
    }

    // Check if a fast path was committed for a round that hasn't advanced
    const recoveryTag = fastPathRoundRef.current;
    if (!recoveryTag) {
      return;
    }

    // Clear the tag immediately to prevent double-recovery
    fastPathRoundRef.current = null;

    // Only recover if we're not already spinning the next round
    if (isSpinning) {
      return;
    }


    // 1. Undo dedup so the round can be re-processed
    animatedRoundsRef.current.delete(recoveryTag);
    animationCompletedRef.current = false;
    isAnimatingRef.current = true; // Lock to prevent race with main effect

    // 2. Reset counter states that the fast path set
    dispatch(setCounterAnimationStarted(false));
    dispatch(setCounterAnimationFinished(false));

    // 3. Hide the fast-path WinText flash
    if (globalLineWinTextRef?.current) {
      globalLineWinTextRef.current.hide();
    }

    // 4. Run normal win processing
    if (!currentRound?.wins || !winningLines) {
      isAnimatingRef.current = false;
      animationCompletedRef.current = true;
      return;
    }

    const winningData =
      reelsetControllerRef.current.matchHorizontalWinningLines(
        winningLines,
        currentRound.wins,
      );

    if (!winningData || Object.keys(winningData).length === 0) {
      isAnimatingRef.current = false;
      animationCompletedRef.current = true;
      return;
    }

    const winTypeLocal = currentRound?.winType || null;
    const isAnimatedWinLocal = currentRound?.isAnimatedWin || false;

    dispatch(setCriticalAnimationPlaying(false));
    dispatch(setIsAnimatedWin(isAnimatedWinLocal));
    dispatch(setCounterAnimationStarted(true));

    if (FEATURES_CONFIG.CHARACTER_ANIMATION.ENABLED) {
      characterAnimationControllerRef.current?.playAnimation("yes");
    }

    const shouldShowAllWins = FEATURES_CONFIG.SHOW_ALL_WINS.ENABLED;
    const isScatterWin = currentRound?.type === "feature";
    const skipShowAllWins =
      isScatterWin && FEATURES_CONFIG.SHOW_ALL_WINS.EXCLUDE_SCATTER;

    (async () => {
      let shouldProceed = true;

      if (shouldShowAllWins && !skipShowAllWins) {
        if (winTypeLocal && !winSoundPlayedRef.current) {
          if (isAnimatedWinLocal) {
            winSoundPlayedRef.current = true;
          }
        }

        if (isAnimatedWinLocal) {
          dispatch(closeAllPopups());
          dispatch(setSymbolPopupBlocked(true));
          dispatch(setWinAnimationsPlaying(true));
        }

        shouldProceed = await showAllWinningSymbolsOnceRef.current(
          winningData,
          false,
          [],
          winTypeLocal,
          isAnimatedWinLocal,
        );
      }

      if (shouldProceed) {
        if (!shouldShowAllWins || skipShowAllWins) {
          isAnimatingRef.current = false;
        }
        startWinningLinesAnimationRef.current(winningData);
      } else {
        if (
          FEATURES_CONFIG.WIN_LINES.ENABLED &&
          winLinesContainerRef.current
        ) {
          winLinesContainerRef.current.hideWinLine();
        }
        if (globalLineWinTextRef?.current) {
          globalLineWinTextRef.current.hide();
        }
        if (lineWinTextRef?.current) {
          lineWinTextRef.current.hide();
        }
      }

      if (!shouldShowAllWins || skipShowAllWins) {
        dispatch(setCounterAnimationFinished(true));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spacebarTurboActive, autoPlayActive]);

  /**
   * Executes a callback function on each reel controller.
   */
  const executeOnReelControllers = useCallback(
    (callback) => {
      const reels = reelRefs.current;
      if (!reels) return;

      for (let i = 0; i < reels.length; i++) {
        const controller = reels[i]?.getReelController();
        if (controller) {
          callback(controller, i);
        }
      }
    },
    [reelRefs],
  );

  /**
   * Executes a callback function on each overlay reel controller.
   */
  const executeOnOverlayReelControllers = useCallback(
    (callback) => {
      const overlayReels = overlayReelRefs.current;
      if (!overlayReels) return;

      for (let i = 0; i < overlayReels.length; i++) {
        const controller = overlayReels[i]?.getOverlayReelController();
        if (controller) {
          callback(controller, i);
        }
      }
    },
    [overlayReelRefs],
  );

  useEffect(() => {
    executeOnReelControllersRef.current = executeOnReelControllers;
    executeOnOverlayReelControllersRef.current =
      executeOnOverlayReelControllers;
  }, [executeOnReelControllers, executeOnOverlayReelControllers]);

  /**
   * Resets all visual effects and animations to their default state.
   * @param {boolean} instant - If true, quick sequential fade before cleanup (used on spin start)
   */
  const resetAllEffects = useCallback(async (instant = false) => {
    // Step 1: Fade dim overlay FIRST — must be gone before we mutate layers underneath
    if (dimReelSetRef.current) {
      await dimReelSetRef.current.undimReels(instant);
    }

    // Step 2: Now safe to clear win overlays and restore textures
    if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
      winLinesContainerRef.current.hideWinLine();
    }

    if (FEATURES_CONFIG.WIN_BOX.ENABLED && winBoxContainerRef.current) {
      winBoxContainerRef.current.hideWinBoxes();
    }

    executeOnOverlayReelControllersRef.current((overlayReelController) => {
      if (overlayReelController) {
        if (overlayReelController.hideAllAnimations) {
          overlayReelController.hideAllAnimations();
        }

        if (
          FEATURES_CONFIG.EXPANDING_WILDS.ENABLED &&
          overlayReelController.isExpandingWildVisible
        ) {
          overlayReelController.forceStopExpandingWildFullReel?.();
        }
      }
    });

    executeOnReelControllersRef.current((controller) => {
      if (controller && controller.showAllSymbolTextures) {
        controller.showAllSymbolTextures();
      }
    });

    // NEW - unified line win text
    if (lineWinTextRef?.current) {
      lineWinTextRef.current.hide();
    }

    // NOTE: globalLineWinTextRef is intentionally NOT hidden here.
    // It self-cleans via its spine "out" animation → _finalizeOut().
    // If a new win arrives, showWinAmount() replaces it seamlessly.

    // Clear previous win positions for diff tracking
    prevWinPosSetRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalLineWinTextRef, lineWinTextRef]);

  /**
   * Matches winning line data with game layout winning lines.
   * Groups multiple wins on the same line together and returns structured data for animation.
   */
  const matchHorizontalWinningLines = useCallback((winningLines, wins) => {
    const result = {};
    if (!Array.isArray(wins)) {
      return result;
    }

    for (let i = 0; i < wins.length; i++) {
      const win = wins[i];
      const { id, positions, symbol, win: winAmount } = win;

      if (!Array.isArray(positions)) {
        continue;
      }

      if (!result[i]) {
        result[i] = {
          line: id != null ? winningLines[id] : null, // Scatter wins have no line (id is null)
          positions: [],
          symbol,
          win: winAmount,
          lineId: id, // Preserve original line index for win_line spine animation
          isScatter: !!win.isScatter, // Preserve scatter flag from RGS
          isCoin: !!win.isCoin, // Preserve coin trigger flag from RGS
        };
      }
      result[i].positions.push(...positions);
    }

    return result;
  }, []);

  /**
   * Syncs refs with current state values for use in async operations.
   */
  useEffect(() => {
    spinCompleteRef.current = spinComplete;
    currentRoundRef.current = currentRound;
    winningLinesRef.current = winningLines;
    dispatchRef.current = dispatch;

    turboActiveRef.current = spacebarTurboActive || autoPlayActive;
    infoPopupActiveRef.current = infoPopupActive;
  }, [
    spinComplete,
    currentRound,
    winningLines,
    dispatch,

    spacebarTurboActive,
    autoPlayActive,
    infoPopupActive,
  ]);

  /**
   * Initializes the reelset controller object once on component mount.
   * Creates lazy-loaded getters for controllers and attaches utility methods.
   */
  useEffect(() => {
    if (reelsetControllerRef.current) {
      return;
    }

    reelsetControllerRef.current = {
      _reelControllers: null,
      _overlayReelControllers: null,

      get reelControllers() {
        if (!this._reelControllers) {
          this._reelControllers =
            reelRefs.current
              ?.map((reel) => reel?.getReelController())
              .filter(Boolean) || [];
        }
        return this._reelControllers;
      },

      get overlayReelControllers() {
        if (!this._overlayReelControllers) {
          this._overlayReelControllers =
            overlayReelRefs.current
              ?.map((overlayReel) => overlayReel?.getOverlayReelController())
              .filter(Boolean) || [];
        }
        return this._overlayReelControllers;
      },

      executeOnReelControllers,
      executeOnOverlayReelControllers,
      resetAllEffects,
      matchHorizontalWinningLines,

      /**
       * Synchronous full visual cleanup — everything in ONE frame, no async.
       * Called by handleSpin before reels start so no previous-win artifacts remain.
       */
      snapClearAll: () => {
        // Kill async win cycling loop — prevents re-dimming after clear
        isAnimatingRef.current = false;
        // Dim overlay
        if (dimReelSetRef.current?.snapClear) {
          dimReelSetRef.current.snapClear();
        }
        // Win lines
        if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
          winLinesContainerRef.current.hideWinLine();
        }
        // Win boxes
        if (FEATURES_CONFIG.WIN_BOX.ENABLED && winBoxContainerRef.current) {
          winBoxContainerRef.current.hideWinBoxes();
        }
        // Overlay reel animations
        executeOnOverlayReelControllersRef.current((oc) => {
          if (oc?.hideAllAnimations) oc.hideAllAnimations();
          if (FEATURES_CONFIG.EXPANDING_WILDS.ENABLED && oc?.isExpandingWildVisible) {
            oc.forceStopExpandingWildFullReel?.();
          }
        });
        // Restore symbol textures
        executeOnReelControllersRef.current((c) => {
          if (c?.showAllSymbolTextures) c.showAllSymbolTextures();
        });
        // Line win text (per-line, not the global total)
        if (lineWinTextRef?.current) lineWinTextRef.current.hide();
        // NOTE: globalLineWinTextRef is intentionally NOT cleared here.
        // It self-cleans via its spine "out" animation → _finalizeOut().
        // This allows WinText to persist into the next spin (overlapping reels),
        // matching reference game behavior. If a new win arrives before "out"
        // finishes, showWinAmount() replaces it seamlessly.
        // Win position tracking
        prevWinPosSetRef.current = null;
      },
    };

    isReadyRef.current = true;

    // Register the synchronous cleanup on the shared ref so
    // ReelControlContext.handleSpin can call it before starting reels.
    snapClearAllRef.current = reelsetControllerRef.current.snapClearAll;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Cycles through winning lines one by one, displaying animations and win amounts.
   * Shows win text, restores textures for non-winning symbols, and plays animations for winning symbols.
   */
  const cycleThroughLines = useCallback(
    async (
      winningData,
      lineIds,
      isFirstEverCycle = false,
      startIndex = 0,
      onFirstLineComplete = null,
    ) => {
      for (let index = startIndex; index < lineIds.length; index++) {
        if (!isAnimatingRef.current) {
          break;
        }

        const lineId = lineIds[index];
        currentLineIndexRef.current = index;
        const data = winningData[lineId];

        if (!data) {
          continue;
        }

        // Play win_line sound only for numbered lines (scatter has null id)
        if (
          FEATURES_CONFIG.WIN_LINES.ENABLED &&
          SOUND_NAME.win_line &&
          data.lineId !== undefined &&
          data.lineId !== null
        ) {
          soundManager.play(SOUND_NAME.win_line);
        }

        // Find a target reel for positioning (prefer middle, then second reel)
        let targetReelIndex = null;

        for (let i = 0; i < data.positions.length; i++) {
          const [reelIndex] = data.positions[i];
          if (reelIndex === GAME_LAYOUT.MIDDLE_REEL) {
            targetReelIndex = GAME_LAYOUT.MIDDLE_REEL;
            break;
          }
        }

        if (targetReelIndex === null) {
          for (let i = 0; i < data.positions.length; i++) {
            const [reelIndex] = data.positions[i];
            if (reelIndex === GAME_LAYOUT.SECOND_REEL) {
              targetReelIndex = GAME_LAYOUT.SECOND_REEL;
              break;
            }
          }
        }

        // Hide previous line's win text before showing next
        if (lineWinTextRef?.current) {
          lineWinTextRef.current.hide();
        }
        if (globalLineWinTextRef?.current) {
          globalLineWinTextRef.current.hide();
        }

        // Show win amount for this line (skip zero-win entries like synthetic coin triggers)
        if (data.win && lineWinTextRef?.current) {
          const lineWinTextOptions = {};
          // Speed up WinText to match fast win duration
          if (useFastWinRef.current && FEATURES_CONFIG.WIN_ANIMATION_TIMING.WIN_FAST?.WIN_TEXT_SPEED_UP) {
            lineWinTextOptions.targetDuration =
              FEATURES_CONFIG.WIN_ANIMATION_TIMING.SPINE_LOOP_DURATION;
          }
          lineWinTextRef.current.showWinAmount(data.win, lineWinTextOptions);
        }

        const currentLinePositions = data.positions.map(
          ([reelIndex, symbolIndex]) => {
            const reelController =
              reelsetControllerRef.current.reelControllers[reelIndex];
            const symbol =
              reelController?.symbolsContainer?.children?.[symbolIndex];
            const symbolId = symbol?.currentSymbolId || symbol?.symbolId;
            return [reelIndex, symbolIndex, symbolId];
          },
        );

        // Pre-compute winning position Set for O(1) lookups
        const winPosSet = new Set(
          currentLinePositions.map(([r, s]) => `${r}_${s}`),
        );
        const reelsInCurrentLine = new Set(
          currentLinePositions.map(([reelIndex]) => reelIndex),
        );

        reelsetControllerRef.current.executeOnOverlayReelControllers(
          (overlayReelController) => {
            if (!overlayReelController?.isExpandingWildVisible) {
              overlayReelController?.hideAllAnimationsExcept(
                currentLinePositions,
              );
            }
          },
        );

        if (
          FEATURES_CONFIG.EXPANDING_WILDS.ENABLED &&
          FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "full_reel" &&
          expandingWildReelIndicesRef.current.length > 0
        ) {
          const reelsArray = Array.from(reelsInCurrentLine).sort(
            (a, b) => a - b,
          );
          let rightmostConnected = -1;
          for (let i = 0; i < reelsArray.length; i++) {
            const currentReel = reelsArray[i];

            if (i === 0 && currentReel > 1) {
              break;
            }

            if (i > 0 && currentReel !== reelsArray[i - 1] + 1) {
              break;
            }

            rightmostConnected = currentReel;
          }

          expandingWildReelIndicesRef.current.forEach((wildReelIndex) => {
            const overlayReelRef = overlayReelRefs.current?.[wildReelIndex];
            const wildReelHasWinningPosition =
              reelsInCurrentLine.has(wildReelIndex);
            const shouldBeBright =
              wildReelHasWinningPosition && rightmostConnected >= wildReelIndex;
            const currentlyBright =
              expandingWildAnimationStateRef.current[wildReelIndex] ?? true;

            if (shouldBeBright && !currentlyBright) {
              overlayReelRef?.playExpandingWildWinAnimation?.();
              expandingWildAnimationStateRef.current[wildReelIndex] = true;
            } else if (!shouldBeBright && currentlyBright) {
              overlayReelRef?.playExpandingWildDarkAnimation?.();
              expandingWildAnimationStateRef.current[wildReelIndex] = false;
            }
          });
        }

        if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
          winLinesContainerRef.current.hideWinLine();
        }

        if (FEATURES_CONFIG.WIN_BOX.ENABLED && winBoxContainerRef.current) {
          winBoxContainerRef.current.hideWinBoxes();
        }

        // Diff-based texture transitions — only mutate symbols that changed between lines
        const prevSet = prevWinPosSetRef.current;
        const reelIndices = GAME_LAYOUT.REEL_INDICES;

        if (prevSet) {
          // Show textures for positions that WERE winning but no longer are
          for (const prevKey of prevSet) {
            if (!winPosSet.has(prevKey)) {
              const [reel, symbolIndex] = prevKey.split("_").map(Number);
              const reelController =
                reelsetControllerRef.current.reelControllers[reel];
              if (reelController?.showSymbolTexture) {
                reelController.showSymbolTexture(symbolIndex);
              }
            }
          }
        } else {
          // First line — show all non-winning textures (full reset)
          for (let ri = 0; ri < reelIndices.length; ri++) {
            const reel = reelIndices[ri];
            const reelController =
              reelsetControllerRef.current.reelControllers[reel];
            if (!reelController) continue;

            for (
              let symbolIndex = 0;
              symbolIndex < GAME_LAYOUT.SYMBOLS_VISIBLE;
              symbolIndex++
            ) {
              if (!winPosSet.has(`${reel}_${symbolIndex}`)) {
                if (reelController.showSymbolTexture) {
                  reelController.showSymbolTexture(symbolIndex);
                }
              }
            }
          }
        }

        // Sync win animations and hide textures for current winning positions
        // Pre-compute line display window for per-symbol loop calculation
        const lineLoopsConfig = FEATURES_CONFIG.WIN_ANIMATION_TIMING.LOOPS_PER_LINE || 1;
        const lineMasterMs = SpineAnimationData.getMasterWinDuration();
        const lineFastMs = SpineAnimationData.getFastWinDuration();
        const lineAnimType = useFastWinRef.current
          ? (FEATURES_CONFIG.WIN_ANIMATION_TIMING.WIN_FAST?.ANIMATION_NAME || "win_fast")
          : "win";
        const lineDisplayWindowMs = ((useFastWinRef.current && lineFastMs !== null) ? lineFastMs : lineMasterMs) * lineLoopsConfig;

        const positions = data.positions;
        for (let i = 0; i < positions.length; i++) {
          const [reelIndex, symbolIndex] = positions[i];
          const overlayReelController =
            reelsetControllerRef.current.overlayReelControllers[reelIndex];
          const reelController =
            reelsetControllerRef.current.reelControllers[reelIndex];

          if (!overlayReelController || !reelController) continue;

          const hasExpandingWildFullReel =
            FEATURES_CONFIG.EXPANDING_WILDS.ENABLED &&
            FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "full_reel" &&
            overlayReelController.isExpandingWildVisible === true;

          if (hasExpandingWildFullReel) {
            continue;
          }

          // CRITICAL: Read from spinningSprites (which has wild replacements)
          const VISIBLE_SYMBOL_START_INDEX = 1 + GAME_LAYOUT.SYMBOLS_VISIBLE;
          const spriteIndex = VISIBLE_SYMBOL_START_INDEX + symbolIndex;
          const sprite = reelController.spinningSprites?.[spriteIndex];

          if (sprite) {
            const actualSymbolId = sprite.currentSymbolId;
            if (actualSymbolId !== undefined) {
              // Per-symbol loop count: how many times this symbol's animation fits in the line display window
              const symbolWinMs = SpineAnimationData.getSymbolWinDuration(actualSymbolId, useFastWinRef.current);
              const perSymbolLoops = (symbolWinMs && symbolWinMs > 0 && lineDisplayWindowMs > 0)
                ? Math.max(1, Math.round(lineDisplayWindowMs / symbolWinMs))
                : 1;

              // Feature trigger lines (scatter/coin) must loop indefinitely until
              // the intro dialog takes over — finite loops cause play→stop→restart jitter.
              const isFeatureTriggerLine =
                (data.isScatter && store.getState().featureGame.freeSpinTriggered) ||
                (data.isCoin && (
                  store.getState().featureGame.royalCollectTriggered ||
                  store.getState().potCollection.potCollectionTriggered
                ));
              const effectiveLoops = isFeatureTriggerLine ? 0 : perSymbolLoops;

              overlayReelController.syncWinAnimation(
                symbolIndex,
                actualSymbolId,
                lineAnimType,
                null,
                effectiveLoops,
              );
            }
          }

          if (reelController.hideSymbolTexture) {
            reelController.hideSymbolTexture(symbolIndex);
          }
        }

        // Store current positions for next line's diff
        prevWinPosSetRef.current = winPosSet;

        if (
          FEATURES_CONFIG.WIN_LINES.ENABLED &&
          winLinesContainerRef.current &&
          data.lineId !== undefined &&
          data.lineId !== null
        ) {
          winLinesContainerRef.current.showWinLine(data.lineId);
        }

        // Show win boxes — either on winning positions only or full line path
        if (
          FEATURES_CONFIG.WIN_BOX.ENABLED &&
          winBoxContainerRef.current &&
          data.line
        ) {
          const winBoxPositions = FEATURES_CONFIG.WIN_BOX.WINNING_ONLY
            ? data.positions // Only matched/winning symbol positions
            : data.line.map((row, reelIndex) => [reelIndex, row]); // Full line path

          // Compute target loops from pre-cached durations
          let winBoxTargetLoops = 0;
          const winBoxAnimMs = SpineAnimationData.getWinBoxDuration();
          if (FEATURES_CONFIG.WIN_BOX.SNAP_TO_LOOP && winBoxAnimMs > 0) {
            const fastWinDur = SpineAnimationData.getFastWinDuration();
            const baseDur = (useFastWinRef.current && fastWinDur !== null)
              ? fastWinDur
              : SpineAnimationData.getMasterWinDuration();
            const lineLoops = FEATURES_CONFIG.WIN_ANIMATION_TIMING.LOOPS_PER_LINE || 1;
            winBoxTargetLoops = Math.max(1, Math.floor((baseDur * lineLoops) / winBoxAnimMs));
          }

          winBoxContainerRef.current.showWinBoxes(winBoxPositions, winBoxTargetLoops);
        }

        await new Promise((resolve) => {
          // Duration from SpineAnimationData (extracted once at init)
          const targetLoops =
            FEATURES_CONFIG.WIN_ANIMATION_TIMING.LOOPS_PER_LINE || 1;
          const masterDuration = SpineAnimationData.getMasterWinDuration();
          const fastWinDuration = SpineAnimationData.getFastWinDuration();
          const baseDuration = (useFastWinRef.current && fastWinDuration !== null)
            ? fastWinDuration
            : masterDuration;
          let lineDuration = baseDuration * targetLoops;

          // Snap to complete win box loops to prevent partial-loop jitter
          const winBoxAnimMs = SpineAnimationData.getWinBoxDuration();
          if (
            FEATURES_CONFIG.WIN_BOX.ENABLED &&
            FEATURES_CONFIG.WIN_BOX.SNAP_TO_LOOP &&
            winBoxAnimMs > 0
          ) {
            const completeLoops = Math.floor(lineDuration / winBoxAnimMs);
            if (completeLoops >= 1) {
              lineDuration = completeLoops * winBoxAnimMs;
            }
          }

          // Play win_line a second time at half the line duration
          if (
            FEATURES_CONFIG.WIN_LINES.ENABLED &&
            SOUND_NAME.win_line &&
            data.lineId !== undefined &&
            data.lineId !== null
          ) {
            const secondPlayTimeout = new TickerTimeout(() => {
              soundManager.play(SOUND_NAME.win_line);
            }, lineDuration / 2);
            currentTimeoutsRef.current.push(secondPlayTimeout);
          }

          // FREE SPIN TRIGGER: Show intro after scatter line animation finishes
          if (
            FEATURES_CONFIG.FREE_SPINS.ENABLED &&
            data.isScatter &&
            store.getState().featureGame.freeSpinTriggered
          ) {
            // If animated win is still showing (4-5 scatters), wait for it to close first
            const animState = store.getState();
            if (selectWinAnimationsPlaying(animState)) {
              const waitForAnimatedWin = new Promise((resolve) => {
                const poll = () => {
                  const currentState = store.getState();
                  if (!selectWinAnimationsPlaying(currentState)) {
                    resolve();
                  } else {
                    const t = new TickerTimeout(poll, 100);
                    currentTimeoutsRef.current.push(t);
                  }
                };
                poll();
              });
              waitForAnimatedWin.then(() => {
                dispatchRef.current(setShowFreeSpinIntro(true));
              });
            } else {
              const delayTimeout = new TickerTimeout(() => {
                const idx = currentTimeoutsRef.current.indexOf(delayTimeout);
                if (idx > -1) currentTimeoutsRef.current.splice(idx, 1);
                dispatchRef.current(setShowFreeSpinIntro(true));
              }, lineDuration / 1.25);
              currentTimeoutsRef.current.push(delayTimeout);
            }
          }

          // ROYAL COLLECT TRIGGER: Show intro after trigger symbol animation finishes
          if (
            FEATURES_CONFIG.ROYAL_COLLECT.ENABLED &&
            data.isCoin &&
            store.getState().featureGame.royalCollectTriggered
          ) {
            const animState = store.getState();
            if (selectWinAnimationsPlaying(animState)) {
              const waitForAnimatedWin = new Promise((resolve) => {
                const poll = () => {
                  const currentState = store.getState();
                  if (!selectWinAnimationsPlaying(currentState)) {
                    resolve();
                  } else {
                    const t = new TickerTimeout(poll, 100);
                    currentTimeoutsRef.current.push(t);
                  }
                };
                poll();
              });
              waitForAnimatedWin.then(() => {
                dispatchRef.current(setShowRoyalCollectIntro(true));
              });
            } else {
              const delayTimeout = new TickerTimeout(() => {
                const idx = currentTimeoutsRef.current.indexOf(delayTimeout);
                if (idx > -1) currentTimeoutsRef.current.splice(idx, 1);
                dispatchRef.current(setShowRoyalCollectIntro(true));
              }, lineDuration);
              currentTimeoutsRef.current.push(delayTimeout);
            }
          }

          // POT COLLECTION TRIGGER: Show intro after trigger symbol animation finishes
          // Skip for mid-bonus reload — we go straight to bonus scene
          if (
            FEATURES_CONFIG.POT_COLLECTION.ENABLED &&
            data.isCoin &&
            store.getState().potCollection.potCollectionTriggered &&
            store.getState().potCollection.reloadPhase !== "respins"
          ) {

            // Dispatch PC intro flag immediately. PotCollectionIntroDialog gates
            // its startSequence on !infoPopupActive so if FR info popup is still
            // open, the intro waits for it to close naturally via React re-render.
            const showPCIntroWhenReady = () => {

              dispatchRef.current(setShowPotCollectionIntro(true));
            };

            const animState = store.getState();
            if (selectWinAnimationsPlaying(animState)) {

              const waitForAnimatedWin = new Promise((resolve) => {
                const poll = () => {
                  const currentState = store.getState();
                  if (!selectWinAnimationsPlaying(currentState)) {
                    resolve();
                  } else {
                    const t = new TickerTimeout(poll, 100);
                    currentTimeoutsRef.current.push(t);
                  }
                };
                poll();
              });
              waitForAnimatedWin.then(() => {

                showPCIntroWhenReady();
              });
            } else {

              const delayTimeout = new TickerTimeout(() => {
                const idx = currentTimeoutsRef.current.indexOf(delayTimeout);
                if (idx > -1) currentTimeoutsRef.current.splice(idx, 1);
                showPCIntroWhenReady();
              }, lineDuration);
              currentTimeoutsRef.current.push(delayTimeout);
            }
          }

          const timeoutId = new TickerTimeout(() => {
            const idx = currentTimeoutsRef.current.indexOf(timeoutId);
            if (idx > -1) currentTimeoutsRef.current.splice(idx, 1);
            resolve();
          }, lineDuration);
          currentTimeoutsRef.current.push(timeoutId);
        });

        // After the first line's animation completes, fire the callback to unblock spin
        if (index === 0 && onFirstLineComplete) {
          onFirstLineComplete();
          onFirstLineComplete = null; // Only fire once
        }


      }
    },
    [
      winLinesContainerRef,
      winBoxContainerRef,
      globalLineWinTextRef,
      overlayReelRefs,
      lineWinTextRef,
    ],
  );

  /**
   * Shows all winning symbols simultaneously before line cycling begins.
   * Creates a celebration effect by displaying all winning animations at once.
   */
  const showAllWinningSymbolsOnce = useCallback(
    async (
      winningData,
      hasExpandingWilds,
      expandingWildReelIndices = [],
      winType = null,
      isAnimatedWin = false,
    ) => {
      const allPositions = [];
      const lineIds = Object.keys(winningData);

      lineIds.forEach((lineId) => {
        const data = winningData[lineId];
        data.positions.forEach((position) => {
          const [reelIndex, symbolIndex] = position;
          const exists = allPositions.some(
            ([r, s]) => r === reelIndex && s === symbolIndex,
          );
          if (!exists) {
            allPositions.push(position);
          }
        });
      });

      // Targeted cleanup: hide landing/idle animations on NON-winning positions only.
      // Build a set of winning positions per reel so we can exclude them.
      const winPositionsByReel = new Map();
      allPositions.forEach(([reelIndex, symbolIndex]) => {
        if (!winPositionsByReel.has(reelIndex)) {
          winPositionsByReel.set(reelIndex, new Set());
        }
        winPositionsByReel.get(reelIndex).add(symbolIndex);
      });

      executeOnOverlayReelControllersRef.current(
        (overlayReelController, reelIndex) => {
          if (overlayReelController) {
            const exceptPositions = winPositionsByReel.get(reelIndex) || null;
            overlayReelController.hideAllAnimations(exceptPositions);
          }
        },
      );

      executeOnReelControllersRef.current((controller) => {
        if (controller && controller.showAllSymbolTextures) {
          controller.showAllSymbolTextures();
        }
      });

      // Play win sound for non-animated wins (once per win, after expanding wilds complete)
      if (!isAnimatedWin && SOUND_NAME.basic_win) {
        soundManager.play(SOUND_NAME.basic_win);
      }

      // === STEP 3: Determine win animation type (fast vs regular) ===
      const winFastConfig = FEATURES_CONFIG.WIN_ANIMATION_TIMING.WIN_FAST;
      const currentStateForAnim = store.getState();
      const isAutoplayActive = currentStateForAnim.settings.autoPlayActive;
      const useFastWin =
        winFastConfig?.ENABLED &&
        (spacebarTurboActiveRef.current || isAutoplayActive);
      const winAnimationType = useFastWin
        ? (winFastConfig.ANIMATION_NAME || "win_fast")
        : "win";

      // Persist fast-win decision so cycleThroughLines uses the same timing/animation
      useFastWinRef.current = useFastWin;

      // Compute display window duration for "show all wins" phase
      const masterWinMs = SpineAnimationData.getMasterWinDuration();
      const fastWinMs = SpineAnimationData.getFastWinDuration();
      const showAllLoopsConfig = FEATURES_CONFIG.SHOW_ALL_WINS.LOOPS_FOR_SHOW_ALL || 1;
      const displayWindowMs = ((useFastWin && fastWinMs !== null) ? fastWinMs : masterWinMs) * showAllLoopsConfig;

      // Start spine win animations on winning symbols
      for (let i = 0; i < allPositions.length; i++) {
        const [reelIndex, symbolIndex] = allPositions[i];
        const overlayReelController =
          reelsetControllerRef.current.overlayReelControllers[reelIndex];
        const reelController =
          reelsetControllerRef.current.reelControllers[reelIndex];

        if (!overlayReelController || !reelController) {
          continue;
        }

        const hasExpandingWildFullReel =
          FEATURES_CONFIG.EXPANDING_WILDS.ENABLED &&
          FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "full_reel" &&
          overlayReelController.isExpandingWildVisible === true;

        if (hasExpandingWildFullReel) {
          continue;
        }

        const symbol = reelController.symbolsContainer.children[symbolIndex];
        if (symbol) {
          const actualSymbolId = symbol.currentSymbolId || symbol.symbolId;
          if (actualSymbolId !== undefined) {
            // Per-symbol loop count: how many times this symbol's animation fits in the display window
            const symbolWinMs = SpineAnimationData.getSymbolWinDuration(actualSymbolId, useFastWin);
            const perSymbolLoops = (symbolWinMs && symbolWinMs > 0 && displayWindowMs > 0)
              ? Math.max(1, Math.round(displayWindowMs / symbolWinMs))
              : 1;
            overlayReelController.showWinAnimation(symbolIndex, actualSymbolId, winAnimationType, null, perSymbolLoops);
          }
        }
      }

      const isFullReelMode =
        FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "full_reel";
      const isCascadeMode =
        FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "cascade";

      if (!isFullReelMode && expandingWildReelIndices.length > 0) {
        expandingWildReelIndices.forEach((reelIndex) => {
          const reelController =
            reelsetControllerRef.current.reelControllers[reelIndex];
          if (reelController && reelController.showAllSymbolTextures) {
            reelController.showAllSymbolTextures();
          }
        });
      }

      // In cascade mode, transition cascaded wild symbols to "win" animation
      // ONLY if they are part of an actual win line (non-winning wilds stay as static texture)
      if (isCascadeMode && expandingWildReelIndices.length > 0) {
        expandingWildReelIndices.forEach((reelIndex) => {
          const overlayReelController =
            reelsetControllerRef.current.overlayReelControllers[reelIndex];
          const reelController =
            reelsetControllerRef.current.reelControllers[reelIndex];

          if (!overlayReelController || !reelController) return;

          const winningPositionsOnReel = winPositionsByReel.get(reelIndex);

          for (
            let symbolIndex = 0;
            symbolIndex < GAME_LAYOUT.SYMBOLS_VISIBLE;
            symbolIndex++
          ) {
            // Only animate wilds that are part of a winning line
            if (
              !winningPositionsOnReel ||
              !winningPositionsOnReel.has(symbolIndex)
            ) {
              continue;
            }

            const symbol =
              reelController.symbolsContainer.children[symbolIndex];
            if (symbol) {
              const actualSymbolId = symbol.currentSymbolId || symbol.symbolId;

              if (actualSymbolId === WILD_SYMBOL_ID) {
                const symbolWinMs = SpineAnimationData.getSymbolWinDuration(actualSymbolId, useFastWin);
                const perSymbolLoops = (symbolWinMs && symbolWinMs > 0 && displayWindowMs > 0)
                  ? Math.max(1, Math.round(displayWindowMs / symbolWinMs))
                  : 1;
                overlayReelController.showWinAnimation(
                  symbolIndex,
                  actualSymbolId,
                  winAnimationType,
                  null,
                  perSymbolLoops,
                );
              }
            }
          }
        });
      }

      // === STEP 4: Hide textures behind spines (reveal spine animations) ===
      for (let i = 0; i < allPositions.length; i++) {
        const [reelIndex, symbolIndex] = allPositions[i];
        const reelController =
          reelsetControllerRef.current.reelControllers[reelIndex];
        const overlayReelController =
          reelsetControllerRef.current.overlayReelControllers[reelIndex];
        const hasExpandingWildFullReel =
          overlayReelController &&
          FEATURES_CONFIG.EXPANDING_WILDS.ENABLED &&
          FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "full_reel" &&
          overlayReelController.isExpandingWildVisible === true;

        if (hasExpandingWildFullReel) {
          continue;
        }

        if (reelController && reelController.hideSymbolTexture) {
          reelController.hideSymbolTexture(symbolIndex);
        }
      }

      // === STEP 5: Show win lines (on top of active spines) ===
      // === STEP 5: Show win lines (on top of active spines) ===
      if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
        const regularLineIds = lineIds
          .map((key) => winningData[key]?.lineId)
          .filter((id) => id !== undefined && id !== null);
        if (regularLineIds.length > 0) {
          winLinesContainerRef.current.showMultipleWinLines(regularLineIds);
        }
      }

      // === STEP 5b: Show win boxes on all line positions ===
      if (FEATURES_CONFIG.WIN_BOX.ENABLED && winBoxContainerRef.current) {
        const allLinePositions = [];
        lineIds.forEach((key) => {
          const data = winningData[key];
          // Skip coin trigger wins — no winboxes for synthetic trigger entries
          if (data?.isCoin) return;
          if (FEATURES_CONFIG.WIN_BOX.WINNING_ONLY && data?.positions?.length > 0) {
            // WINNING_ONLY: use only matched symbol positions
            allLinePositions.push(data.positions);
          } else if (data?.line) {
            // Full line path
            allLinePositions.push(
              data.line.map((row, reelIndex) => [reelIndex, row]),
            );
          } else if (data?.positions?.length > 0) {
            // Scatter win (no line) — use actual positions directly
            allLinePositions.push(data.positions);
          }
        });
        if (allLinePositions.length > 0) {
          // Show-all-wins: compute win box loops from display window (same timing as symbols)
          const winBoxAnimMs = SpineAnimationData.getWinBoxDuration();
          const winBoxLoops = (FEATURES_CONFIG.WIN_BOX.SNAP_TO_LOOP && winBoxAnimMs > 0 && displayWindowMs > 0)
            ? Math.max(1, Math.floor(displayWindowMs / winBoxAnimMs))
            : 0;
          winBoxContainerRef.current.showAllWinBoxes(allLinePositions, winBoxLoops);
        }
      }

      // === STEP 6: Show win text (overlay on top of everything) ===
      if (globalLineWinTextRef?.current && currentRoundRef.current?.win) {
        const winTextOptions = {};
        // Speed up WinText to fit in→idle→out within fast win duration
        if (useFastWin && winFastConfig?.WIN_TEXT_SPEED_UP) {
          winTextOptions.targetDuration =
            FEATURES_CONFIG.WIN_ANIMATION_TIMING.SPINE_LOOP_DURATION; // 1000ms (matches win_fast)
        }
        globalLineWinTextRef.current.showWinAmount(currentRoundRef.current.win, winTextOptions);
      }

      // === STEP 7: Dim reels LAST (everything is ready, dim reveals the full composition) ===
      if (dimReelSetRef.current) {
        dimReelSetRef.current.dimReels(true);
      }

      if (
        FEATURES_CONFIG.EXPANDING_WILDS.ENABLED &&
        FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "full_reel" &&
        expandingWildReelIndices.length > 0
      ) {
        expandingWildReelIndices.forEach((reelIndex) => {
          const overlayReelRef = overlayReelRefs.current?.[reelIndex];
          overlayReelRef?.playExpandingWildWinAnimation?.();
          expandingWildAnimationStateRef.current[reelIndex] = true;
        });
      }

      // Event-based timing: use pre-cached durations from SpineAnimationData
      await new Promise((resolve) => {
        const masterDuration = SpineAnimationData.getMasterWinDuration();
        const fastWinDuration = SpineAnimationData.getFastWinDuration();

        const targetLoops = hasExpandingWilds
          ? FEATURES_CONFIG.EXPANDING_WILDS.LOOPS_FOR_EXPANDING_WILD_SHOW_ALL ||
            1
          : FEATURES_CONFIG.SHOW_ALL_WINS.LOOPS_FOR_SHOW_ALL || 1;

        const activeDuration = (useFastWin && fastWinDuration !== null)
          ? fastWinDuration
          : masterDuration;
        const displayDuration = activeDuration * targetLoops;

        // Unblock spin button — timing depends on input mode:
        // Manual mode: unblock after SPIN_UNBLOCK_DELAY (1000ms) so user can hold spacebar to turbo.
        // Autoplay / spacebar turbo: unblock at full displayDuration so animations complete cleanly before next spin.
        let unblockDelay;
        const currentState = store.getState();
        const isAutoplay = currentState.settings.autoPlayActive;

        if (spacebarTurboActiveRef.current || isAutoplay) {
          // Both spacebar turbo and autoplay: unblock at full display duration.
          // The continuous effect fires immediately on counterAnimationFinished — no extra SPIN_DELAY needed.
          unblockDelay = displayDuration;
        } else {
          // Manual mode: fixed delay so user can hold spacebar to turbo after 1s
          unblockDelay = FEATURES_CONFIG.WIN_ANIMATION_TIMING.SPIN_UNBLOCK_DELAY;
        }

        const unblockTimeoutId = new TickerTimeout(() => {
          const idx = currentTimeoutsRef.current.indexOf(unblockTimeoutId);
          if (idx > -1) currentTimeoutsRef.current.splice(idx, 1);
          dispatch(setCounterAnimationFinished(true));
        }, unblockDelay);
        currentTimeoutsRef.current.push(unblockTimeoutId);

        // Full display duration — animations keep playing after spin unblocks
        const displayTimeoutId = new TickerTimeout(() => {
          const index = currentTimeoutsRef.current.indexOf(displayTimeoutId);
          if (index > -1) {
            currentTimeoutsRef.current.splice(index, 1);
          }
          resolve();
        }, displayDuration);
        currentTimeoutsRef.current.push(displayTimeoutId);
      });

      // Release the early animation lock so startWinningLinesAnimation can take over
      isAnimatingRef.current = false;

      // Use spacebarTurboActiveRef (synchronous) instead of turboActiveRef (useEffect-batched).
      // turboActiveRef can be stale if user released spacebar but React hasn't re-rendered yet.


      if (spacebarTurboActiveRef.current) {
        return false;
      }

      return true;
    },
    [
      globalLineWinTextRef,
      dimReelSetRef,
      winLinesContainerRef,
      winBoxContainerRef,
      overlayReelRefs,
      dispatch,
      spacebarTurboActiveRef,
    ],
  );



  /**
   * Starts the win line animation cycle.
   * Cycles through each winning line, showing animations and win amounts.
   * For feature rounds, reorders lines to show scatter first.
   */
  const startWinningLinesAnimation = useCallback(
    async (winningData) => {
      if (isAnimatingRef.current) {
        return;
      }

      const lineIds = Object.keys(winningData);
      const winTypeLocal = winType;
      const isAnimatedWinLocal = isAnimatedWin;

      if (winTypeLocal && !winSoundPlayedRef.current) {
        if (isAnimatedWinLocal) {
          winSoundPlayedRef.current = true;
        }
      }

      isAnimatingRef.current = true;
      // Force-complete counter state before clearing timeouts.
      // clearAllTimeouts() kills the pending unblock timeout (line 2093-2097)
      // that would have dispatched counterAnimationFinished(true). Without this,
      // the turbo Continuous Effect stalls waiting for a transition that never comes.
      const animState = store.getState().animations;
      if (animState.counterAnimationStarted && !animState.counterAnimationFinished) {
        dispatch(setCounterAnimationFinished(true));
      }
      clearAllTimeouts();

      if (globalLineWinTextRef?.current) {
        globalLineWinTextRef.current.hide();
      }

      // Clear previous win positions for clean first-line diff
      prevWinPosSetRef.current = null;

      if (dimReelSetRef.current) {
        dimReelSetRef.current.dimReels(true);
      }

      winningDataRef.current = winningData;
      lineIdsRef.current = lineIds.slice();
      currentLineIndexRef.current = 0;

      const runSeamlessAnimationCycle = async () => {
        while (isAnimatingRef.current) {
          try {
            // On first cycle, pass callback to unblock spin after first line completes
            const unblockCallback = isFirstAnimationRef.current
              ? () => dispatchRef.current(setCounterAnimationFinished(true))
              : null;

            await cycleThroughLines(
              winningData,
              lineIds,
              isFirstAnimationRef.current,
              0,
              unblockCallback,
            );

            isFirstAnimationRef.current = false;
            currentLineIndexRef.current = 0;
          } catch (error) {
            // SAFETY NET: If the animation cycle errors out before onFirstLineComplete fired,
            // dispatch counterAnimationFinished(true) to prevent permanent spin button lockup.
            // Without this, the button stays blocked forever because winNotFullyDisplayed stays true.
            if (isFirstAnimationRef.current) {
              dispatchRef.current(setCounterAnimationFinished(true));
              isFirstAnimationRef.current = false;
            }
            break;
          }
        }
      };

      // FREE SPIN TRIGGER: Play lines linearly once (0 → scatter) and stop.
      // Scatter is always the last line, so we end on it naturally.
      if (
        FEATURES_CONFIG.FREE_SPINS.ENABLED &&
        store.getState().featureGame.freeSpinTriggered
      ) {
        // Ensure animated win is tracked — for scatter-only rounds (4-5 scatters),
        // showAllWinningSymbolsOnce is skipped so winAnimationsPlaying was never set.
        // Without this, the scatter detection wait in cycleThroughLines can't catch it.
        if (isAnimatedWin) {
          dispatchRef.current(setWinAnimationsPlaying(true));
        }

        const unblockCallback = isFirstAnimationRef.current
          ? () => dispatchRef.current(setCounterAnimationFinished(true))
          : null;

        // When show-all-wins is active, all payline wins were already displayed
        // simultaneously — skip straight to the trigger line (always last).
        const triggerStartIndex = FEATURES_CONFIG.SHOW_ALL_WINS.ENABLED
          ? lineIds.length - 1
          : 0;

        try {
          await cycleThroughLines(
            winningData,
            lineIds,
            isFirstAnimationRef.current,
            triggerStartIndex,
            unblockCallback,
          );
        } catch (error) {
          // SAFETY NET: Same as runSeamlessAnimationCycle — ensure spin button unblocks on error
          if (isFirstAnimationRef.current) {
            dispatchRef.current(setCounterAnimationFinished(true));
          }
        }
        isFirstAnimationRef.current = false;
        return;
      }

      // ROYAL COLLECT TRIGGER: Play lines linearly once (0 → coin trigger) and stop.
      // Coin trigger is always the last line, so we end on it naturally — same as free spin scatters.
      if (
        FEATURES_CONFIG.ROYAL_COLLECT.ENABLED &&
        store.getState().featureGame.royalCollectTriggered
      ) {
        if (isAnimatedWin) {
          dispatchRef.current(setWinAnimationsPlaying(true));
        }

        const unblockCallback = isFirstAnimationRef.current
          ? () => dispatchRef.current(setCounterAnimationFinished(true))
          : null;

        // When show-all-wins is active, skip straight to the trigger line (always last).
        const triggerStartIndex = FEATURES_CONFIG.SHOW_ALL_WINS.ENABLED
          ? lineIds.length - 1
          : 0;

        try {
          await cycleThroughLines(
            winningData,
            lineIds,
            isFirstAnimationRef.current,
            triggerStartIndex,
            unblockCallback,
          );
        } catch (error) {
          if (isFirstAnimationRef.current) {
            dispatchRef.current(setCounterAnimationFinished(true));
          }
        }
        isFirstAnimationRef.current = false;
        return;
      }

      // POT COLLECTION TRIGGER: Same pattern as RC — play lines linearly once.
      if (
        FEATURES_CONFIG.POT_COLLECTION.ENABLED &&
        store.getState().potCollection.potCollectionTriggered
      ) {
        if (isAnimatedWin) {
          dispatchRef.current(setWinAnimationsPlaying(true));
        }

        const unblockCallback = isFirstAnimationRef.current
          ? () => dispatchRef.current(setCounterAnimationFinished(true))
          : null;

        // When show-all-wins is active, skip straight to the trigger line (always last).
        const triggerStartIndex = FEATURES_CONFIG.SHOW_ALL_WINS.ENABLED
          ? lineIds.length - 1
          : 0;

        try {
          await cycleThroughLines(
            winningData,
            lineIds,
            isFirstAnimationRef.current,
            triggerStartIndex,
            unblockCallback,
          );
        } catch (error) {
          if (isFirstAnimationRef.current) {
            dispatchRef.current(setCounterAnimationFinished(true));
          }
        }
        isFirstAnimationRef.current = false;
        return;
      }

      runSeamlessAnimationCycle();
    },
    [
      clearAllTimeouts,
      cycleThroughLines,
      dimReelSetRef,
      dispatch,
      globalLineWinTextRef,
      winType,
      isAnimatedWin,
    ],
  );

  const cycleThroughLinesRef = useRef(null);
  cycleThroughLinesRef.current = cycleThroughLines;

  const showAllWinningSymbolsOnceRef = useRef(null);
  showAllWinningSymbolsOnceRef.current = showAllWinningSymbolsOnce;



  const startWinningLinesAnimationRef = useRef(null);
  startWinningLinesAnimationRef.current = startWinningLinesAnimation;



  /**
   * Resets all animations and effects when a new spin starts.
   */
  useEffect(() => {
    if (freeSpinActive) {
      return;
    }

    if (!isSpinning || !reelsetControllerRef.current) return;

    const wasAnimating = isAnimatingRef.current;

    // ── Synchronous visual cleanup ──────────────────────────────────

    // Must complete in the SAME frame before reels start spinning.
    // Previously resetAllEffects(true) was async (100ms undim fade),
    // which let reels begin while dim overlay was still fading.

    // 1. Snap dim overlay off instantly (no fade)
    if (dimReelSetRef.current?.snapClear) {
      dimReelSetRef.current.snapClear();
    }

    // 2. Hide win lines
    if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
      winLinesContainerRef.current.hideWinLine();
    }

    // 3. Hide win boxes
    if (FEATURES_CONFIG.WIN_BOX.ENABLED && winBoxContainerRef.current) {
      winBoxContainerRef.current.hideWinBoxes();
    }

    // 4. Hide overlay reel animations
    executeOnOverlayReelControllersRef.current((oc) => {
      if (oc?.hideAllAnimations) oc.hideAllAnimations();
      if (FEATURES_CONFIG.EXPANDING_WILDS.ENABLED && oc?.isExpandingWildVisible) {
        oc.forceStopExpandingWildFullReel?.();
      }
    });

    // 5. Restore symbol textures
    executeOnReelControllersRef.current((c) => {
      if (c?.showAllSymbolTextures) c.showAllSymbolTextures();
    });

    // 6. Hide line win text
    if (lineWinTextRef?.current) lineWinTextRef.current.hide();
    // NOTE: globalLineWinTextRef is intentionally NOT hidden here.
    // It self-cleans via its spine "out" animation → _finalizeOut().
    // If a new win arrives, showWinAmount() replaces it seamlessly.

    // 7. Clear win position tracking
    prevWinPosSetRef.current = null;

    // ── End visual cleanup ──────────────────────────────────────────

    // Always clear all timeouts — catches orphaned timeouts from prior cycles
    clearAllTimeouts();

    if (wasAnimating) {
      isAnimatingRef.current = false;
    }

    dispatch(setWinAnimationsPlaying(false));
    dispatch(setIsAnimatedWin(false));
    dispatch(setCriticalAnimationPlaying(false));
    dispatch(setExpandingWildsOccurred(false));
    dispatch(setSkipWinAnimation(false));
    dispatch(setUserWantsToDismissWinAnimation(false));
    dispatch(setSkipCounterAnimation(false));
    dispatch(setIsWaitingForUserConfirmation(false));

    animationCompletedRef.current = false;
    fastPathRoundRef.current = null; // Clear fast path tag — new spin starts clean
    isFirstAnimationRef.current = true;
    animatedRoundsRef.current.clear();
    winSoundPlayedRef.current = false;
    expandingWildReelIndicesRef.current = [];
    expandingWildAnimationStateRef.current = {};
    winningDataRef.current = null;
    lineIdsRef.current = [];
    currentLineIndexRef.current = 0;

    useFastWinRef.current = false;

    soundManager.stop(SOUND_NAME.expanding_wild);
    if (FEATURES_CONFIG.WIN_LINES.ENABLED) {
      soundManager.stop(SOUND_NAME.win_line);
    }
  }, [
    isSpinning,

    freeSpinActive,
    clearAllTimeouts,
    dispatch,
    dimReelSetRef,
    winLinesContainerRef,
    winBoxContainerRef,
    overlayReelRefs,
    globalLineWinTextRef,
    lineWinTextRef,
  ]);

  /**
   * Resets visual effects when returning from free spins feature.
   */
  useEffect(() => {
    if (resetMainGameVisualEffects) {
      if (reelsetControllerRef.current?.reelControllers) {
        reelsetControllerRef.current.resetAllEffects();

        if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
          winLinesContainerRef.current.hideWinLine();
        }

        if (FEATURES_CONFIG.WIN_BOX.ENABLED && winBoxContainerRef.current) {
          winBoxContainerRef.current.hideWinBoxes();
        }
      }

      if (isAnimatingRef.current) {
        isAnimatingRef.current = false;
        clearAllTimeouts();
      }

      animationCompletedRef.current = false;
      isFirstAnimationRef.current = true;
      animatedRoundsRef.current.clear();


      dispatch(setResetMainGameVisualEffects(false));
    }
  }, [
    resetMainGameVisualEffects,
    clearAllTimeouts,
    dispatch,
    winLinesContainerRef,
    winBoxContainerRef,
  ]);

  /**
   * Shows turbo suggestion popup on non-winning rounds when appropriate.
   */
  useEffect(() => {
    if (!FEATURES_CONFIG.TURBO_SUGGESTION.ENABLED) {
      return;
    }

    if (
      turboSuggestionPending &&
      spinComplete &&
      !isSpinning &&
      !freeSpinActive
    ) {
      if (!hasWins) {
        // Check if user previously declined — don't show again
        const uiSettings = StorageManager.loadUISettings();
        if (!uiSettings.turboSuggestionDeclined) {
          dispatch(openTurboSuggestionPopup());
        }
        dispatch(setTurboSuggestionPending(false));
      }
    }
  }, [
    turboSuggestionPending,
    spinComplete,
    isSpinning,
    freeSpinActive,
    currentRound,
    dispatch,
    hasWins,
  ]);

  /**
   * Cleanup effect: Clears all timeouts and resets effects when component unmounts.
   */
  useEffect(() => {
    return () => {
      clearAllTimeouts();
      isAnimatingRef.current = false;

      if (reelsetControllerRef.current) {
        reelsetControllerRef.current.resetAllEffects();
      }
    };
  }, [clearAllTimeouts]);

  return {
    reelsetController: reelsetControllerRef.current,
  };
};

export default useWinController;
